import { z } from 'zod'

type ImportedKeyword = { title?: string; keys: string[]; text: string }

const entrySchema = z.object({
  title: z.string().optional(),
  keys: z.array(z.string()).optional(),
  key: z.array(z.string()).optional(),
  text: z.string().optional(),
  content: z.string().optional(),
  enabled: z.boolean().optional(),
  disable: z.boolean().optional(),
})

export function parseLorebook(text: string): ImportedKeyword[] {
  const source = text.trim()
  if (!source) throw new Error('키워드북 내용을 넣어 주세요.')
  if (/^[\[{]/.test(source)) {
    let json: unknown
    try {
      json = JSON.parse(source)
    } catch {
      throw new Error(
        'JSON 형식을 확인해 주세요. 닫는 괄호나 쉼표가 빠졌을 수 있어요.'
      )
    }
    const envelope = z.object({ entries: z.unknown() }).safeParse(json)
    const raw = envelope.success ? envelope.data.entries : json
    const record = z.record(z.string(), z.unknown()).safeParse(raw)
    const rows = Array.isArray(raw)
      ? raw
      : record.success
        ? Object.values(record.data)
        : []
    if (!rows.length)
      throw new Error(
        '가져올 키워드북 항목이 없어요. 키워드·내용 형식을 확인해 주세요.'
      )
    return rows.map((row, index) => {
      const parsed = entrySchema.safeParse(row)
      if (
        !parsed.success ||
        !(parsed.data.keys ?? parsed.data.key)?.length ||
        !(parsed.data.text ?? parsed.data.content)?.trim()
      )
        throw new Error(
          `${index + 1}번째 항목의 키워드와 내용을 확인해 주세요.`
        )
      if (parsed.data.enabled === false || parsed.data.disable === true)
        throw new Error(
          '비활성 항목이 있어요. 가져올 항목만 남기거나 텍스트 형식으로 정리해 주세요.'
        )
      const keys = (parsed.data.keys ?? parsed.data.key ?? [])
        .map((key) => key.trim())
        .filter(Boolean)
      if (!keys.length)
        throw new Error(`${index + 1}번째 항목의 키워드가 비어 있어요.`)
      return {
        ...(parsed.data.title !== undefined
          ? { title: parsed.data.title }
          : {}),
        keys,
        text: parsed.data.text ?? parsed.data.content ?? '',
      }
    })
  }
  const entries: ImportedKeyword[] = []
  let current: ImportedKeyword | null = null
  let title: string | undefined
  const lines = text.split(/\r?\n/)
  for (const [index, line] of lines.entries()) {
    // 항목 제목은 구조 표시이며, 본문 안의 일반 소제목은 유지한다.
    if (/^\s*#{3,6}\s+\S/.test(line)) {
      const next = lines.slice(index + 1).find((row) => row.trim()) ?? ''
      if (/^\s*(?:키워드|발동\s*키워드|keywords?|keys?)\s*[:：]/i.test(next)) {
        title = line.replace(/^\s*#{3,6}\s+/, '').trim()
        continue
      }
    }
    const match =
      /^\s*(?:#{1,6}\s*)?(?:키워드|발동\s*키워드|keywords?|keys?)\s*[:：]\s*(.+)$/i.exec(
        line
      )
    if (match) {
      current = {
        ...(title ? { title } : {}),
        keys: match[1]
          .split(/[,，]/)
          .map((key) => key.trim())
          .filter(Boolean),
        text: '',
      }
      entries.push(current)
      title = undefined
    } else if (current) {
      const content = line.replace(/^\s*(?:내용|content)\s*[:：]\s*/i, '')
      current.text += (current.text ? '\n' : '') + content
    } else if (line.trim())
      throw new Error(
        '각 항목을 “키워드: 이름, 별칭”과 “내용: 설정” 형식으로 나눠 주세요.'
      )
  }
  if (
    !entries.length ||
    entries.some((entry) => !entry.keys.length || !entry.text.trim())
  )
    throw new Error('항목마다 키워드와 내용이 모두 필요해요.')
  return entries.map((entry) => ({
    ...entry,
    text: entry.text.replace(/^\n+|\n+$/g, ''),
  }))
}

export function formatLorebook(entries: ImportedKeyword[]) {
  return JSON.stringify(entries)
}
